<template>
  <div class="youtube-controls" v-if="canManageYoutube">
    <div class="youtube-status-line">
      <span class="badge" :class="hasPlaylist ? 'bg-success' : 'bg-secondary'">
        <i :class="hasPlaylist ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'" aria-hidden="true"></i>
        {{ hasPlaylist ? $t('competitions.youtube.playlist_created') : $t('competitions.youtube.playlist_pending') }}
      </span>
      <span v-if="displayPlaylistId" class="playlist-id">{{ displayPlaylistId }}</span>
    </div>
    <div class="youtube-actions">
      <button
          class="btn btn-sm btn-outline-primary"
          :disabled="youtubePreparing || hasPlaylist"
          @click="prepareYoutube"
      >
        <i :class="youtubePreparing ? 'fa-solid fa-spinner fa-spin' : 'fa-brands fa-youtube'"></i>
        {{ $t('competitions.btn_prepare_youtube') || 'Preparar YouTube' }}
      </button>
      <button
          class="btn btn-sm btn-outline-success"
          :disabled="!hasPlaylist || imageUploading || Boolean(bannerUploaded)"
          @click="uploadYoutubeBanner"
      >
        <i :class="imageUploading ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-image'"></i>
        {{ $t('competitions.youtube.upload_banner') }}
      </button>
    </div>
    <div class="youtube-extra">
      <small v-if="bannerUploaded" class="text-success">
        {{ $t('competitions.youtube.banner_uploaded', { date: formatDateShort(bannerUploaded) }) || 'Banner subido' }}
      </small>
      <small v-else class="text-muted">
        {{ $t('competitions.youtube.banner_pending') || 'Banner pendiente' }}
      </small>
      <small v-if="youtubeStatusMessage" class="d-block">{{ youtubeStatusMessage }}</small>
      <small v-for="(issue, idx) in youtubeIssues" :key="'yt-issue-'+idx" class="text-danger d-block">{{ issue }}</small>
      <small v-for="(issue, idx) in imageIssues" :key="'img-issue-'+idx" class="text-danger d-block">{{ issue }}</small>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isSupervisor } from '@/helpers/roleUtils';

export default {
  name: 'CompetitionYoutubeControls',
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      youtubePlaylistId: '',
      imageUploadedAt: null,
      youtubePreparing: false,
      imageUploading: false,
      youtubeStatusMessage: '',
      youtubeIssues: [],
      imageIssues: [],
    };
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    competitionPlaylist() {
      return this.competition?.competitionPlaylist || this.competition?.competition_playlist || null;
    },
    hasPlaylist() {
      return Boolean(this.youtubePlaylistId || this.competitionPlaylist?.youtube_playlist_id);
    },
    bannerUploaded() {
      return this.imageUploadedAt || this.competitionPlaylist?.image_uploaded_at || this.competitionPlaylist?.imagen_upload_at || null;
    },
    canManageYoutube() {
      return isSupervisor(this.user);
    },
    displayPlaylistId() {
      return this.youtubePlaylistId || this.competitionPlaylist?.youtube_playlist_id || '';
    },
    authToken() {
      const direct = this.token;
      if (direct) return direct;
      const fromStorage = (localStorage.getItem('token') || '').replace(/"/g, '');
      return fromStorage || '';
    }
  },
  methods: {
    async prepareYoutube() {
      const competitionId = this.competition?.id;
      if (!competitionId) return;
      if (!this.authToken) {
        this.youtubeStatusMessage = this.$t('competitions.messages.upload_missing_token') || 'Falta token de autenticacion';
        this.youtubeIssues = [];
        return;
      }
      this.youtubePreparing = true;
      this.youtubeStatusMessage = '';
      this.youtubeIssues = [];
      try {
        const response = await this.$axios.post(`/competitions/${competitionId}/playlist/prepare`, {}, {
          headers: { Authorization: `Bearer ${this.authToken}` },
        });
        if (response?.data?.success) {
          this.youtubePlaylistId = response.data.playlist_id || response.data.id || '';
          this.imageUploadedAt = response.data.image_uploaded_at || response.data.imagen_upload_at || null;
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.playlist_created');
          this.youtubeIssues = response?.data?.issues || [];
        } else {
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.prepare_failed');
          this.youtubeIssues = response?.data?.issues || [];
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 503) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_youtube_503');
        } else if (status === 502) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_prepare_502');
        } else {
          this.youtubeStatusMessage = err?.response?.data?.message || this.$t('competitions.youtube.error_prepare_generic');
        }
        this.youtubeIssues = err?.response?.data?.issues || [];
      } finally {
        this.youtubePreparing = false;
      }
    },
    async uploadYoutubeBanner() {
      const competitionId = this.competition?.id;
      if (!competitionId) return;
      if (!this.authToken) {
        this.youtubeStatusMessage = this.$t('competitions.messages.upload_missing_token') || 'Falta token de autenticacion';
        this.imageIssues = [];
        return;
      }
      if (!this.hasPlaylist) {
        this.youtubeStatusMessage = this.$t('competitions.youtube.error_prepare_first');
        return;
      }
      this.imageUploading = true;
      this.imageIssues = [];
      try {
        const response = await this.$axios.post(`/competitions/${competitionId}/playlist/image`, {}, {
          headers: { Authorization: `Bearer ${this.authToken}` },
        });
        if (response?.data?.success) {
          this.imageUploadedAt = response.data.image_uploaded_at || response.data.imagen_upload_at || new Date().toISOString();
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.banner_upload_success');
          this.imageIssues = response?.data?.issues || [];
        } else {
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.banner_upload_failed');
          this.imageIssues = response?.data?.issues || [];
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 503) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_youtube_503');
        } else if (status === 502) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_banner_502');
        } else {
          this.youtubeStatusMessage = err?.response?.data?.message || this.$t('competitions.youtube.error_banner_generic');
        }
        this.imageIssues = err?.response?.data?.issues || [];
      } finally {
        this.imageUploading = false;
      }
    },
    formatDateShort(raw) {
      if (!raw) return '';
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return raw;
      return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    }
  }
};
</script>

<style scoped>
.youtube-controls {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin: 10px 0;
}
.youtube-status-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.playlist-id {
  font-size: 0.9rem;
  color: #374151;
}
.youtube-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.youtube-extra small {
  display: block;
}
</style>
